import { toaster } from './toaster';
import React from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning' | 'loading';

export interface ShowToastOptions {
  title?: string | React.ReactNode;
  description: string | React.ReactNode;
  type?: ToastType;
  duration?: number;
  closable?: boolean;
}

/**
 * Utility function to show toast notifications.
 * Use for transient, dismissable messages like success confirmations or errors.
 * 
 * @example
 * ```tsx
 * showToast({
 *   description: 'Profile updated successfully',
 *   type: 'success',
 * });
 * 
 * showToast({
 *   title: 'Error',
 *   description: 'Failed to save changes',
 *   type: 'error',
 *   duration: 5000,
 * });
 * ```
 */
export function showToast({
  title,
  description,
  type = 'info',
  duration = 3000,
  closable = true,
}: ShowToastOptions) {
  toaster.create({
    title,
    description,
    type,
    duration,
    closable,
  });
}

/**
 * Show a success toast notification
 */
export function showSuccessToast(description: string | React.ReactNode, title?: string | React.ReactNode) {
  showToast({ description, title, type: 'success' });
}

/**
 * Show an error toast notification
 */
export function showErrorToast(description: string | React.ReactNode, title?: string | React.ReactNode) {
  showToast({ description, title, type: 'error', duration: 5000 });
}

/**
 * Show an info toast notification
 */
export function showInfoToast(description: string | React.ReactNode, title?: string | React.ReactNode) {
  showToast({ description, title, type: 'info' });
}

/**
 * Show a warning toast notification
 */
export function showWarningToast(description: string | React.ReactNode, title?: string | React.ReactNode) {
  showToast({ description, title, type: 'warning' });
}
